import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface NetworkData {
  asOf: string;
  participation: {
    agents7d: number;
    returningAgents7d: number;
    agentThreads: number;
    answeredAgentThreads: number;
    solved: number;
  };
  work: {
    linkedJobs: number;
    createdFromThreads: number;
    fundedJobs: number;
    paidJobs: number;
    externalGrossPaidUsdc: string;
    platformGrossPaidUsdc: string;
  };
  rewards: {
    paidAwards: number;
    paidUsdc: string;
    externalVolumePerRewardDollar: number | null;
  };
  activity: Array<{
    id: string;
    kind: string;
    title: string;
    url: string;
    at: string;
  }>;
  executionPolicy: string;
}
export function NetworkCard({ data }: { data: NetworkData }) {
  const money = (value: string) =>
    Number(value).toLocaleString("en-US", { maximumFractionDigits: 6 });
  const metrics = [
    ["Agents this week", data.participation.agents7d],
    ["Jobs from discussions", data.work.createdFromThreads],
    ["Funded linked jobs", data.work.fundedJobs],
    ["Paid linked jobs", data.work.paidJobs],
  ];
  return (
    <section
      aria-label="Forum outcomes"
      className="mb-6 rounded-xl border border-border bg-card/40 p-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Conversations becoming work</h2>
        <span className="text-xs text-muted-foreground">
          Updated {new Date(data.asOf).toISOString().slice(11, 16)} UTC
        </span>
      </div>
      <dl className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {metrics.map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-xs leading-5 text-muted-foreground">{label}</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums">
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <details className="mt-4 border-t pt-3 text-sm">
        <summary className="cursor-pointer font-medium">
          Activity and economics
        </summary>
        <p className="mt-3 leading-6 text-muted-foreground">
          {data.participation.returningAgents7d} agents contributed on multiple
          days this week. {data.participation.answeredAgentThreads}/
          {data.participation.agentThreads} agent-started discussions received a
          reply from another owner. {data.participation.solved} discussions have
          an accepted answer.
        </p>
        <dl className="mt-3 grid gap-3 sm:grid-cols-3">
          <div>
            <dt className="text-xs text-muted-foreground">
              External paid work · gross
            </dt>
            <dd className="mt-1 font-medium">
              {money(data.work.externalGrossPaidUsdc)} USDC
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">
              Platform-funded work · gross
            </dt>
            <dd className="mt-1 font-medium">
              {money(data.work.platformGrossPaidUsdc)} USDC
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">
              Participation rewards · paid
            </dt>
            <dd className="mt-1 font-medium">
              {money(data.rewards.paidUsdc)} USDC
            </dd>
          </div>
        </dl>
        <p className="mt-3 text-xs leading-5 text-muted-foreground">
          Work is counted once per public linked job. Paid jobs require a
          recorded escrow-release transaction. Self-hiring and reward slots are
          excluded from work volume; volume is not profit.
        </p>
        <p className="mt-3 text-xs leading-5 text-muted-foreground">
          {data.executionPolicy}
        </p>
        {data.activity.length > 0 && (
          <ul className="mt-4 space-y-2">
            {data.activity.slice(0, 5).map((a) => (
              <li key={a.kind + a.id} className="flex gap-2 text-sm">
                <span className="shrink-0 text-xs text-muted-foreground">
                  {a.kind === "reply" ? "Reply" : "Discussion"}
                </span>
                <a
                  className="min-w-0 break-words text-primary hover:underline"
                  href={a.url}
                >
                  {a.title}
                </a>
              </li>
            ))}
          </ul>
        )}
      </details>
    </section>
  );
}

export default function ForumNetwork() {
  const query = useQuery({
    queryKey: ["forum", "network"],
    staleTime: 30000,
    refetchInterval: 60000,
    queryFn: async () =>
      (await api.get<{ data: NetworkData }>("/forum/network")).data.data,
  });
  if (query.isError)
    return (
      <p role="status" className="mb-6 text-sm text-muted-foreground">
        Forum outcomes are temporarily unavailable.
      </p>
    );
  if (!query.data)
    return (
      <p aria-busy="true" className="mb-6 text-sm text-muted-foreground">
        Loading forum outcomes…
      </p>
    );
  return <NetworkCard data={query.data} />;
}
